import { Search } from 'lucide-react'
import type { WorkAreaOption } from 'modules/admin/api/plotPlanService'
import { Button } from 'shared/ui/button'
import { Input } from 'shared/ui/input'
import { Label } from 'shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import { DatePicker } from 'modules/permit/components/DatePicker'
import type { FilterState } from './types'

interface ClosePermitFiltersProps {
  filters: FilterState
  workAreaOptions: WorkAreaOption[]
  permitTypeOptions: Array<{ value: string; label: string }>
  statusOptions: Array<{ value: string; label: string }>
  onChange: (next: FilterState) => void
  onSearch: () => void
  onReset: () => void
}

export function ClosePermitFilters({
  filters,
  workAreaOptions,
  permitTypeOptions,
  statusOptions,
  onChange,
  onSearch,
  onReset,
}: ClosePermitFiltersProps) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="space-y-4 p-4">
        <div className="grid gap-3 lg:grid-cols-[1.5fr,180px,180px,180px,220px,160px]">
          <div className="space-y-2">
            <Label htmlFor="close-search">ค้นหา</Label>
            <div className="relative">
              <Search className="absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
              <Input
                id="close-search"
                className="pl-9"
                placeholder="ค้นหา Permit ID, รายละเอียดงาน หรือชื่อผู้ขอ..."
                value={filters.search}
                onChange={(event) => onChange({ ...filters, search: event.target.value })}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    onSearch()
                  }
                }}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>พื้นที่</Label>
            <Select value={filters.workAreaId} onValueChange={(value) => onChange({ ...filters, workAreaId: value })}>
              <SelectTrigger>
                <SelectValue placeholder="เลือกพื้นที่" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ทุกพื้นที่</SelectItem>
                {workAreaOptions.map((item) => (
                  <SelectItem key={item.locationId} value={item.locationId}>
                    {item.name || item.code || item.locationId}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>ประเภท</Label>
            <Select value={filters.permitType} onValueChange={(value) => onChange({ ...filters, permitType: value })}>
              <SelectTrigger>
                <SelectValue placeholder="เลือกประเภท" />
              </SelectTrigger>
              <SelectContent>
                {permitTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>สถานะ</Label>
            <Select value={filters.status} onValueChange={(value) => onChange({ ...filters, status: value })}>
              <SelectTrigger>
                <SelectValue placeholder="เลือกสถานะ" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="close-work-date">เลือกวันที่เข้าทำงาน</Label>
            <DatePicker
              id="close-work-date"
              value={filters.workDate}
              onChange={(date) => onChange({ ...filters, workDate: date })}
              placeholder="เลือกวันที่"
              allowPastDates
            />
          </div>

          <div className="space-y-2">
            <Label className="opacity-0">ค้นหาข้อมูล</Label>
            <Button className="w-full" onClick={onSearch}>
              <Search className="mr-2 h-4 w-4" />
              ค้นหาข้อมูล
            </Button>
          </div>
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={onReset}>
            ล้างตัวกรอง
          </Button>
        </div>
      </div>
    </div>
  )
}
